import React from "react";
import { Row, Container, Col } from "react-bootstrap";
import { PrintButton } from "../../components/printButton";
import { t } from "../../localization/localization";
import { useLocation, useParams } from "react-router-dom";
import DashboardItem from "../../components/dashboardItem";
import { useResearcherBibliometrics } from "../../hooks/rs/useResearcherBibliometrics";
import { numFormatter } from "../../utils/fundFormatter";
import { getCountOfGivenData } from "../../utils/formatChecker";

const currencyFormatters = {
  USD: new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  EUR: new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  TRY: new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
};

const Dashboard = () => {
  const search = useLocation().search;
  const urlSearchParam = new URLSearchParams(search);
  const crisId = urlSearchParam.get("crisId");
  const locale = urlSearchParam.get("locale");

  const { isLoading, data, error } = useResearcherBibliometrics(crisId);

  const renderFundRow = (fund) => {
    return currencyFormatters[fund.currency].format(fund.sum) + "\n";
  };

  return (
    <div>
      {isLoading ? (
        <p>{t(locale, "/common/Loading")}</p>
      ) : data ? (
        <Container>
          <img
            src={`/stats/${window._env_.ENV_NAME}/logo.png`}
            style={{ height: 48, marginBottom: 8 }}
          />
          <Row>
            <Col>
              <PrintButton />
            </Col>
          </Row>
          <Row>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.scholarlyOutput)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/scholarlyOutput")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.articles)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/journalArticles")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.citationCount)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/scopus-citations")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <div style={{ marginTop: " 20x" }}>
                <h1
                  style={{ color: "#001F54", fontSize: 20, marginTop: "10px" }}
                >
                  {numFormatter(getCountOfGivenData(data.viewCount))}/
                  {numFormatter(getCountOfGivenData(data.downloadCount))}
                </h1>
                <h6 style={{ color: "#275DAD", paddingTop: "10px" }}>
                  {t(locale, "/rs/dashboard/viewsDownloads")}
                </h6>
              </div>
            </DashboardItem>
            <DashboardItem popoverContent="Bir akademisyenin en az h sayıda atıf alan h sayıda yayını olduğu anlamına gelmektedir. Örnek: h-indeksi 20 olan bir akademisyenin, 20 tane en az 20 atıf alan yayını varken, h-indeksi 8 olan bir akademisyenin ise 8’in üzerinde atıf alan en az 8 yayını var anlamındadır. Bir akademisyenin h-indeksinin bir puan artması için, örneğin 8’den 9’a çıkması için hem ilk sekizde yer alan yayınlarının atıf sayısının 8 ve üzerine çıkması hem de ilk sekizde yer almayan başka bir çalışmasının en az 9 atıf alması gerekmektedir.">
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.hindex)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/hIndex")}
              </h6>
            </DashboardItem>
            <DashboardItem popoverContent="Google Scholar tarafından bulunan akademik puanlama türüdür. Bu puanlamada yazarın sadece 10 ve üzeri atıf almış bilimsel makaleleri değerlendirilmeye alınır. 10 ve üzeri atıf almış makale sayısı i10-indeksi değerini verir.">
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.i10Index)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/i10Index")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {numFormatter(getCountOfGivenData(data.openAccessCount))}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/openAccessCount")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.supervisedMScTheses)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/supervisedMscTheses")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.supervisedPhDTheses)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/supervisedPhdTheses")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.patents)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/patents")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.projects)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/projects")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.citationsPerPublication).toFixed(2)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/citationsPerPublication")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.distinctJournals)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/distinctJournals")}
              </h6>
            </DashboardItem>
            <DashboardItem>
              <h1 style={{ color: "#001F54" }}>
                {getCountOfGivenData(data.distinctJournalCategories)}
              </h1>
              <h6 style={{ color: "#275DAD" }}>
                {t(locale, "/rs/dashboard/distinctJournalCategories")}
              </h6>
            </DashboardItem>
          </Row>
          <Row>
            <Col className="">
              * {t(locale, "/rs/dashboard/CitationIndexMetrics")}{" "}
              <a target="_blank" href="https://www.scopus.com/">
                {t(locale, "/ou/dashboard/Scopus")}
              </a>{" "}
              {t(locale, "/rs/dashboard/CalculatedFromTheData")}
            </Col>
          </Row>
        </Container>
      ) : (
        <div>
          {t(locale, "/common/error")}: {t(locale, "/common/UnknownError")}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
